import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Database } from "lucide-react";

const ALLERGENS = [
  "Gluten", "Crustacis", "Ous", "Peix", "Cacauets",
  "Soja", "Làctics", "Fruits secs", "Api", "Mostassa",
  "Sèsam", "Sulfits", "Tramús", "Mol·luscs",
];

export function RecipeIngredients({
  form, setForm,
  ingredientCatalog, ingSearch, setIngSearch,
  addIngredient, removeIngredient, updateIngredient,
  handleIngCostBlur, selectFromCatalog,
  totalIngGrams, autoIngCost, hasIngCosts,
}) {
  const productWeight = parseFloat(form.weight_unit) || 0;
  const weightDiff = productWeight > 0 ? totalIngGrams - productWeight : null;

  return (
    <>
      {/* Ingredients */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <Label>Ingredients i cost</Label>
            <p className="text-[10px] text-gray-400 mt-0.5">Afegiu el cost unitari per calcular automàticament el cost del plat</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={addIngredient} className="gap-1 text-[#7B2D3F]" data-testid="recipe-ingredient-add-btn">
            <Plus className="w-3 h-3" /> Afegir
          </Button>
        </div>
        <div className="grid grid-cols-[1fr_52px_44px_52px_44px_24px] gap-1 mb-1 px-1">
          <p className="text-[9px] font-semibold text-gray-400 uppercase">Ingredient</p>
          <p className="text-[9px] font-semibold text-gray-400 uppercase">Qty</p>
          <p className="text-[9px] font-semibold text-gray-400 uppercase">Un.</p>
          <p className="text-[9px] font-semibold text-gray-400 uppercase">€/u</p>
          <p className="text-[9px] font-semibold text-gray-400 uppercase">Sub.</p>
          <span />
        </div>
        <div className="space-y-1.5">
          {form.ingredients.map((ing, idx) => {
            const unitCost = parseFloat(ing.unit_cost) || 0;
            const qty = parseFloat(ing.quantity) || 0;
            const catalogIng = ingredientCatalog.find(c => c.name?.toLowerCase() === ing.name?.toLowerCase());

            let ingTotal = 0;
            if (catalogIng && unitCost > 0) {
              const unitWeight = parseFloat(catalogIng.unit_weight) || 1;
              ingTotal = (qty / unitWeight) * unitCost;
            } else {
              ingTotal = unitCost;
            }

            const pct = autoIngCost > 0 && ingTotal > 0 ? (ingTotal / autoIngCost * 100) : 0;
            const searchVal = ingSearch[idx] !== undefined ? ingSearch[idx] : ing.name;
            const suggestions = searchVal.length >= 1
              ? ingredientCatalog.filter(c => c.name.toLowerCase().includes(searchVal.toLowerCase())).slice(0, 6)
              : [];
            const fromCatalog = ingredientCatalog.some(c => c.name.toLowerCase() === ing.name.toLowerCase());

            return (
              <div key={`ing-${idx}`} className="space-y-0.5">
                <div className="grid grid-cols-[1fr_52px_44px_52px_44px_24px] gap-1 items-center">
                  <div className="relative">
                    <Input
                      placeholder="Nom" value={searchVal}
                      onChange={(e) => { setIngSearch(prev => ({ ...prev, [idx]: e.target.value })); updateIngredient(idx, "name", e.target.value); }}
                      onBlur={() => setTimeout(() => setIngSearch(prev => ({ ...prev, [idx]: undefined })), 200)}
                      className={`h-8 text-sm ${fromCatalog ? "border-emerald-300 bg-emerald-50/50" : ""}`}
                    />
                    {suggestions.length > 0 && ingSearch[idx] !== undefined && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        {suggestions.map(s => (
                          <button key={s.id} type="button" onMouseDown={() => selectFromCatalog(idx, s)} className="w-full text-left px-3 py-1.5 hover:bg-[#7B2D3F]/5 text-xs flex justify-between items-center">
                            <span className="font-medium">{s.name}</span>
                            <span className="text-gray-400">€{s.unit_price.toFixed(3)}/{s.unit}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Input placeholder="100" type="number" value={ing.quantity} onChange={(e) => updateIngredient(idx, "quantity", e.target.value)} className="h-7 text-xs px-1.5" />
                  <Select value={ing.unit || "g"} onValueChange={(v) => updateIngredient(idx, "unit", v)}>
                    <SelectTrigger className="h-7 text-xs px-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["g", "kg", "ml", "l", "u", "cs", "cc"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="0.00" type="number" step="0.001" value={ing.unit_cost || ""} onChange={(e) => updateIngredient(idx, "unit_cost", e.target.value)} onBlur={() => handleIngCostBlur(idx)} className="h-7 text-xs px-1.5" />
                  <div className="text-center">
                    {ingTotal > 0 ? (
                      <span className={`text-[10px] font-bold ${pct > 40 ? "text-red-600" : pct > 25 ? "text-amber-600" : "text-gray-700"}`}>{ingTotal.toFixed(2)}€</span>
                    ) : <span className="text-[10px] text-gray-300">—</span>}
                  </div>
                  {form.ingredients.length > 1 ? (
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeIngredient(idx)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  ) : <span />}
                </div>
                {pct > 0 && (
                  <div className="px-0">
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct > 40 ? "bg-red-400" : pct > 25 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <p className="text-[9px] text-gray-400 text-right mt-0.5">{pct.toFixed(0)}% del cost</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Weight balance */}
        {productWeight > 0 && totalIngGrams > 0 && (
          <div className={`mt-2 p-2.5 rounded-xl border text-xs flex items-center justify-between ${
            weightDiff === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : Math.abs(weightDiff) <= productWeight * 0.05 ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}>
            <span className="font-medium">
              {weightDiff === 0 ? "Ingredients = Pes del producte"
                : weightDiff > 0 ? `Ingredients ${weightDiff.toFixed(0)}g per sobre del pes (${productWeight}g)`
                : `Falten ${Math.abs(weightDiff).toFixed(0)}g d'ingredients per arribar al pes (${productWeight}g)`}
            </span>
            <span className="font-bold ml-2">{totalIngGrams}g / {productWeight}g</span>
          </div>
        )}

        {/* Cost summary */}
        {hasIngCosts && (
          <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500 font-medium">Cost total ingredients (auto):</p>
              <p className="text-sm font-bold text-gray-800">{autoIngCost.toFixed(2)}€</p>
            </div>
            <p className="text-[10px] text-gray-400 mt-0.5">Aquest cost s'usarà automàticament com a cost del plat</p>
            {ingredientCatalog.length > 0 && (
              <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
                <Database className="w-3 h-3" />
                Els ingredients en verd estan vinculats al catàleg central i s'actualitzaran automàticament.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Allergens */}
      <div>
        <Label className="mb-2 block">Al·lèrgens</Label>
        <div className="flex flex-wrap gap-2">
          {ALLERGENS.map(a => (
            <button key={a} type="button" onClick={() => {
              const has = form.allergens.includes(a);
              setForm({ ...form, allergens: has ? form.allergens.filter(x => x !== a) : [...form.allergens, a] });
            }} data-testid={`recipe-allergen-${a.toLowerCase().replace(/[·\s]/g, '-')}-toggle`} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
              form.allergens.includes(a) ? "bg-red-100 text-red-800 border-red-300" : "bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300"
            }`}>
              {a}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
